import React from 'react';
import { Box, Container, Typography, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import { LpHeader, LpFooter, IsThisYou, LpCtaBlock, useScrollFade, fadeStyle, useLpTracking } from './LpShared';
import { trackEvent } from '../analytics';

const PAGE = '/lp/app-partner';

export default function LpAppPartner() {
  const navigate = useNavigate();
  useLpTracking(PAGE);
  const revenueSection = useScrollFade();
  const howSection = useScrollFade();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <LpHeader ctaLabel="Explore the Integration" ctaPath="/contact" pagePath={PAGE} />

      {/* ── Hero ── */}
      <Box sx={{ position: 'relative', minHeight: { xs: 520, md: 660 }, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        <Box component="img" src="/ai-people-tech.png" alt="Daycare app technology" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.16 }} />
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(10,10,18,0.97) 0%, rgba(10,10,18,0.72) 55%, rgba(10,10,18,0.9) 100%)' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, py: { xs: 8, md: 12 }, px: 3 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.25)', borderRadius: 10, px: 2, py: 0.75, mb: 3 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#5ce1e6' }} />
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.1em', textTransform: 'uppercase' }}>For Daycare Platform Partners</Typography>
          </Box>
          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.3rem', md: '3.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', lineHeight: 1.05, mb: 3 }}>
            Your competitors are 6 months<br />
            <Box component="span" sx={{ color: '#5ce1e6' }}>from shipping AI safety. Are you?</Box>
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 20 }, color: '#9499b0', maxWidth: 580, lineHeight: 1.65, mb: 5 }}>
            The daycare platforms that add SMCLA first won't just close more enterprise deals — they'll define the new product baseline. No other platform has shipped native AI behavioral safety detection. That window is closing.
          </Typography>
          <Box>
            <Box
              component="button"
              onClick={() => { trackEvent('cta_click', 'Explore Integration', PAGE, { location: 'lp_hero' }); navigate('/contact'); }}
              sx={{ display: 'inline-block', px: 4, py: 1.5, fontWeight: 700, fontSize: 16, fontFamily: 'Inter, system-ui, sans-serif', bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, border: 'none', cursor: 'pointer', boxShadow: '0 0 24px rgba(92,225,230,0.3)' }}
            >
              Explore the Integration →
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── Is this you? ── */}
      <Box sx={{ py: { xs: 7, md: 10 }, px: 3 }}>
        <Container maxWidth="md">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <IsThisYou items={[
                "Enterprise daycares are starting to list 'AI safety monitoring' as a procurement requirement",
                "You've explored building computer vision in-house — the engineering cost is prohibitive",
                "Your NPS is strong but your feature roadmap looks identical to your top 2 competitors",
                "A key customer asked last quarter why their safety alerts don't surface in your platform",
              ]} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box component="img" src="/ai-tech.png" alt="Platform integration" sx={{ width: '100%', borderRadius: 3, border: '1px solid rgba(255,255,255,0.07)', display: 'block', opacity: 0.8 }} />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Revenue and competitive case ── */}
      <Box ref={revenueSection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 7, md: 10 }, px: 3, ...fadeStyle(revenueSection.inView) }}>
        <Container maxWidth="md">
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1.5 }}>The business case</Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 5 }}>
            New revenue. New accounts. No AI development cost.
          </Typography>
          <Grid container spacing={3}>
            {[
              { num: '$0', label: 'AI development cost on your side', sub: 'SMCLA handles detection models, inference, alert delivery, and infrastructure. You handle UX and distribution.', color: '#4ade80' },
              { num: '#1', label: 'First-mover in AI safety on daycare platforms', sub: 'No Brightwheel, HiMama, or Procare equivalent has shipped native behavioral safety detection. The window is open.', color: '#5ce1e6' },
              { num: '<6wk', label: 'From contract to live integration', sub: 'Dedicated engineering team handles the integration. Most partners go from signed agreement to production in under 6 weeks.', color: '#ff9f43' },
            ].map(({ num, label, sub, color }) => (
              <Grid key={num} size={{ xs: 12, md: 4 }}>
                <Box sx={{ p: 3, bgcolor: '#13131f', border: `1px solid ${color}20`, borderRadius: 3, height: '100%' }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: '2.2rem', color, lineHeight: 1, mb: 1 }}>{num}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.75 }}>{label}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{sub}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── How it works ── */}
      <Box ref={howSection.ref} sx={{ py: { xs: 7, md: 10 }, px: 3, ...fadeStyle(howSection.inView) }}>
        <Container maxWidth="md">
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1.5 }}>How the integration works</Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 5 }}>
            Safety alerts inside your platform. Your UX, your brand.
          </Typography>
          <Stack spacing={2.5}>
            {[
              { num: '1', title: 'API or SDK connection', body: 'SMCLA connects to your platform via REST API or embeddable SDK. Camera feeds route through SMCLA\'s detection infrastructure. No changes to your existing camera or storage setup.' },
              { num: '2', title: 'Alerts surface natively in your dashboard', body: 'When SMCLA detects a flagged event, the alert appears inside your platform\'s existing notification layer. Your design. Your UX. SMCLA powers what\'s behind it — invisibly if you prefer.' },
              { num: '3', title: 'Revenue share from day one', body: 'For every active SMCLA facility subscription through your platform, you receive a revenue share automatically. Enterprise accounts that require safety compliance — the ones you\'ve been losing — now close.' },
            ].map(({ num, title, body }) => (
              <Box key={num} sx={{ display: 'flex', gap: 3, p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.1)', borderRadius: 3, alignItems: 'flex-start', '&:hover': { borderColor: 'rgba(92,225,230,0.25)' }, transition: 'border-color 0.2s' }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 16, color: '#5ce1e6' }}>{num}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: '#f0f0f8', mb: 1 }}>{title}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{body}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      <LpCtaBlock
        headline="Add the feature that closes enterprise deals."
        sub="We work with a small number of integration partners per quarter. If you want to be first in your market, reach out now."
        ctaLabel="Explore the Integration"
        ctaPath="/contact"
        pagePath={PAGE}
      />
      <LpFooter />
    </Box>
  );
}
